import { FC, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { PageTitle } from '../../../_metronic/layout/core';
import {
  InvoiceList,
  InvoiceModel,
  InvoiceStateEnum,
  listInvoices,
  updateInvoice,
  updateInvoiceNumber,
  UpdateInvoiceNumberProps,
  uploadEdiFile,
} from '../../modules/b2b-invoices';
import { toast } from 'react-toastify';
import { DistributorModel, listDistributors } from '../../modules/distributors';
import InvoiceNumberModal from '../../modules/b2b-invoices/components/InvoiceNumberModal';
import { authUserSelector } from '../../modules/auth';
import { useSelector } from 'react-redux';
import invoiceEditingUsers from '../../modules/tasks/constants/invoiceEditingUsers';
import CreditNoteModal from '../../modules/b2b-invoices/components/CreditNoteModal';
import { createCreditNote } from '../../modules/b2b-invoices/services/CreditNoteCRUD';
import Select from 'react-select';
import AmountInvoiceModal, {
  AmountInvoiceKind,
} from '../../modules/b2b-invoices/components/AmountInvoiceModal';
import {
  BulkActionsEnum,
  performBulkAction,
} from '../../modules/b2b-invoices/services/InvoiceCRUD';
import DateRangeSelector from '../../components/DateRangeSelector';

const DEBOUNCE_TIMEOUT = 800;

const InvoicesPage: FC = () => {
  const intl = useIntl();
  const authUser = useSelector(authUserSelector);
  const [distributors, setDistributors] = useState<Array<DistributorModel>>([]);
  const [state, setState] = useState<InvoiceStateEnum | null>(null);
  const [isProforma, setIsProforma] = useState<boolean | null>(null);
  const [isCredit, setIsCredit] = useState<boolean | null>(null);
  const [isManual, setIsManual] = useState<boolean | null>(null);
  const [noteCode, setNoteCode] = useState<string | null>(null);
  const [orderCode, setOrderCode] = useState<string | null>(null);
  const [externalRef, setExternalRef] = useState<string | null>(null);
  const [accounted, setAccounted] = useState<boolean | null>(null);
  const [dateRange, setDateRange] = useState<{
    since: Date | null;
    until: Date | null;
  }>({ since: null, until: null });
  const [invoices, setInvoices] = useState<Array<InvoiceModel>>([]);
  const [loading, setLoading] = useState(false);
  const [selectedDistributor, setSelectedDistributor] =
    useState<DistributorModel | null>(null);
  const [invoiceNumberModalVisible, setInvoiceNumberModalVisible] =
    useState(false);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [visibleCreditNoteModal, setVisibleCreditNoteModal] = useState(false);
  const [visibleCreditNoteAmountModal, setVisibleCreditNoteAmountModal] =
    useState(false);
  const [visibleInvoiceAmountModal, setVisibleInvoiceAmountModal] =
    useState(false);
  const [invoiceForCreditNoteModal, setInvoiceForCreditNoteModal] =
    useState<InvoiceModel | null>(null);
  const [isGeneratingCreditNote, setIsGeneratingCreditNote] = useState(false);
  const distributorOptions = [
    ...distributors.map((distributor) => ({
      value: distributor.id,
      label: distributor.name,
    })),
    {
      value: '',
      label: intl.formatMessage({ id: 'deliveryNotes.anyDistributor' }),
    },
  ];
  const accountedOptions = [
    {
      value: '-1',
      label: intl.formatMessage({ id: 'invoices.allAccountedValues' }),
    },
    {
      value: '1',
      label: intl.formatMessage({ id: 'invoices.accounted' }),
    },
    {
      value: '0',
      label: intl.formatMessage({ id: 'invoices.notAccounted' }),
    },
  ];

  const fetchDistributors = async () => {
    try {
      const { data } = await listDistributors('active');
      setDistributors(data);
    } catch (error) {
      console.warn(error);
    }
  };

  const fetchInvoices = async (withLoader = true) => {
    try {
      if (withLoader) {
        setLoading(true);
      }
      const dateSince = dateRange.since
        ? dateRange.since.toLocaleDateString('en-CA', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          })
        : undefined;
      const dateUntil = dateRange.until
        ? dateRange.until.toLocaleDateString('en-CA', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          })
        : undefined;
      const { data } = await listInvoices({
        distributor: selectedDistributor?.id,
        page,
        state,
        is_proforma: isProforma,
        is_credit: isCredit,
        is_manual: isManual,
        note_code: noteCode?.trim(),
        order_code: orderCode?.trim(),
        external_ref: externalRef?.trim(),
        accounted: accounted,
        date__gte: dateSince,
        date__lte: dateUntil,
      });
      setInvoices(data.results);
      setCount(data.count);
    } catch (error) {
      toast.error(error.message);
    } finally {
      if (withLoader) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchDistributors();
  }, []);

  const listDependencies = [
    selectedDistributor,
    noteCode,
    orderCode,
    externalRef,
    accounted,
    isProforma,
    isCredit,
    isManual,
    state,
    dateRange,
  ];

  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchInvoices();
    }, DEBOUNCE_TIMEOUT);
    return () => clearTimeout(timeout);
  }, [page, ...listDependencies]);

  useEffect(() => {
    setPage(1);
    setCount(0);
  }, listDependencies);

  async function onUpdateState(order: InvoiceModel, state: InvoiceStateEnum) {
    try {
      await updateInvoice({
        ...order,
        state,
      });
      fetchInvoices(false);
      toast.success(intl.formatMessage({ id: 'invoices.updateStateSuccess' }));
    } catch (error) {
      toast.error(intl.formatMessage({ id: 'invoices.updateStateError' }));
      console.warn(error);
    }
  }

  async function onCreateCreditNote(invoice: InvoiceModel) {
    setInvoiceForCreditNoteModal(invoice);
    setVisibleCreditNoteModal(true);
  }

  async function createACreditNote(creditNoteRequest: any) {
    try {
      setIsGeneratingCreditNote(true);
      await createCreditNote(creditNoteRequest);
      toast.success(
        intl.formatMessage({ id: 'invoices.createCreditNoteSuccess' })
      );
      setVisibleCreditNoteModal(false);
    } catch (error) {
      toast.error(intl.formatMessage({ id: 'invoices.createCreditNoteError' }));
      console.warn(error);
    } finally {
      setIsGeneratingCreditNote(false);
    }
  }

  function changeInvoiceNumber() {
    setInvoiceNumberModalVisible(true);
  }

  async function onSaveInvoiceNumber({
    next_num,
    is_credit,
    is_proforma,
  }: UpdateInvoiceNumberProps) {
    try {
      await updateInvoiceNumber({ next_num, is_credit, is_proforma });
      setInvoiceNumberModalVisible(false);
      toast.success(intl.formatMessage({ id: 'invoices.updateNumberSuccess' }));
    } catch (error) {
      toast.error(
        `${intl.formatMessage({ id: 'invoices.updateNumberError' })} ${
          error?.response?.data?.next_num
            ? `: ${error?.response?.data?.next_num}`
            : ''
        }`
      );
      console.warn(error);
    }
  }

  function onCreatedCreditNote() {
    setVisibleCreditNoteAmountModal(false);
    fetchInvoices();
  }

  function onCreatedInvoice() {
    setVisibleInvoiceAmountModal(false);
    fetchInvoices();
  }

  async function onPerformBulkAction(
    action: BulkActionsEnum,
    invoiceIds: number[],
    options?: { responseType?: 'blob' | 'json' }
  ) {
    return performBulkAction({
      invoiceIds,
      action,
      responseType: options?.responseType,
      distributor: selectedDistributor?.id || null,
      state: state || null,
      is_proforma: isProforma,
      is_credit: isCredit,
      is_manual: isManual,
      note_code: noteCode?.trim() || null,
      order_code: orderCode?.trim() || null,
      external_ref: externalRef?.trim() || null,
      accounted: accounted,
      date__gte: dateRange.since
        ? dateRange.since.toLocaleDateString('en-CA', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          })
        : undefined,
      date__lte: dateRange.until
        ? dateRange.until.toLocaleDateString('en-CA', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          })
        : undefined,
    });
  }

  async function onUpdateAccounted(invoice: InvoiceModel, accounted: boolean) {
    try {
      await updateInvoice({
        ...invoice,
        accounted,
      });
      const updatedInvoices = invoices.map((i) =>
        i.id === invoice.id ? { ...i, accounted } : i
      );
      setInvoices(updatedInvoices);
    } catch (error) {
      console.warn(error);
      toast.error(intl.formatMessage({ id: 'invoices.updateAccountedError' }));
    }
  }

  async function onUploadEdi(invoice: InvoiceModel) {
    const confirmMessage = invoice.edi_uploaded_at
      ? intl.formatMessage(
          { id: 'edi.uploadAgainConfirm' },
          {
            code: invoice.number,
            date: new Date(invoice.edi_uploaded_at).toLocaleString(),
          }
        )
      : intl.formatMessage(
          { id: 'edi.uploadConfirm' },
          { code: invoice.number }
        );
    if (!window.confirm(confirmMessage)) return;
    try {
      const { data } = await uploadEdiFile(invoice.id);
      setInvoices((current) =>
        current.map((item) =>
          item.id === invoice.id ? { ...item, ...data } : item
        )
      );
      toast.success(intl.formatMessage({ id: 'edi.uploadSuccess' }));
    } catch (error) {
      console.warn(error);
      // The backend answers a rejected upload with ["<Ediversa reason>"].
      const reason = error?.response?.data;
      toast.error(
        intl.formatMessage(
          { id: 'edi.uploadError' },
          { reason: Array.isArray(reason) ? reason[0] : '' }
        )
      );
    }
  }

  return (
    <>
      <PageTitle breadcrumbs={[]}>
        {intl.formatMessage({ id: 'menu.invoices' })}
      </PageTitle>
      <div className="row">
        <div className="col-12">
          <InvoiceList
            page={page}
            count={count}
            onPageChange={setPage}
            loading={loading}
            invoices={invoices}
            onCreateCreditNote={onCreateCreditNote}
            onUpdateState={onUpdateState}
            onUpdateAccounted={onUpdateAccounted}
            onUploadEdi={onUploadEdi}
            onPerformBulkAction={onPerformBulkAction}
          >
            <div className="d-flex gap-4 flex-wrap">
              <div className="form-group">
                <label className="form-label">
                  {intl.formatMessage({
                    id: 'deliveryNotes.selectDistributor',
                  })}
                </label>
                <Select
                  className="react-select-container w-300px"
                  classNamePrefix="react-select"
                  options={distributorOptions}
                  value={distributorOptions.find(
                    (d) => d.value === selectedDistributor?.id
                  )}
                  onChange={(e) => {
                    const distributorId = e?.value;
                    const distributor = distributors.find(
                      (d) => d.id === distributorId
                    );
                    setSelectedDistributor(distributor);
                  }}
                  placeholder={intl.formatMessage({ id: 'planDetail.select' })}
                />
              </div>
              <div className="form-group d-inline-block">
                <label className="form-label">
                  {intl.formatMessage({ id: 'invoices.selectState' })}
                </label>
                <select
                  className="form-control w-250px form-select"
                  onChange={(e) => {
                    const state = e.target.value;
                    setState(state === '' ? null : (state as InvoiceStateEnum));
                  }}
                >
                  <option value="">
                    {intl.formatMessage({ id: 'invoices.anyState' })}
                  </option>
                  {Object.values(InvoiceStateEnum).map((state) => (
                    <option key={state} value={state}>
                      {intl.formatMessage({
                        id: `invoices.${state.toLowerCase()}`,
                      })}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group d-inline-block">
                <label className="form-label">
                  {intl.formatMessage({ id: 'invoices.selectType' })}
                </label>
                <select
                  className="form-control w-250px form-select"
                  onChange={(e) => {
                    const type = e.target.value;

                    switch (type) {
                      case 'proforma':
                        setIsCredit(false);
                        setIsProforma(true);
                        setIsManual(false);
                        break;
                      case 'credit':
                        setIsProforma(false);
                        setIsCredit(true);
                        setIsManual(false);
                        break;
                      case 'manual':
                        setIsProforma(false);
                        setIsCredit(false);
                        setIsManual(true);
                        break;
                      case 'invoice':
                        setIsProforma(false);
                        setIsCredit(false);
                        setIsManual(false);
                        break;
                      default:
                        setIsProforma(null);
                        setIsCredit(null);
                        setIsManual(null);
                        break;
                    }
                  }}
                >
                  <option value="">
                    {intl.formatMessage({ id: 'invoices.anyType' })}
                  </option>
                  <option value="proforma">
                    {intl.formatMessage({ id: 'invoices.proforma' })}
                  </option>
                  <option value="credit">
                    {intl.formatMessage({ id: 'invoices.credit' })}
                  </option>
                  <option value="invoice">
                    {intl.formatMessage({ id: 'invoices.invoice' })}
                  </option>
                  <option value="manual">
                    {intl.formatMessage({ id: 'invoices.manualInvoice' })}
                  </option>
                </select>
              </div>
              <div className="form-group d-inline-block">
                <label className="form-label">
                  {intl.formatMessage({ id: 'invoices.noteCode' })}
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder={intl.formatMessage({
                    id: 'invoices.noteCodePlaceholder',
                  })}
                  value={noteCode}
                  onChange={(e) => setNoteCode(e.target.value)}
                />
              </div>
              <div className="form-group d-inline-block">
                <label className="form-label">
                  {intl.formatMessage({ id: 'invoices.orderCode' })}
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder={intl.formatMessage({
                    id: 'invoices.orderCodePlaceholder',
                  })}
                  value={orderCode}
                  onChange={(e) => setOrderCode(e.target.value)}
                />
              </div>
              <div className="form-group d-inline-block">
                <label className="form-label">
                  {intl.formatMessage({ id: 'invoices.externalReference' })}
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder={intl.formatMessage({
                    id: 'invoices.externalReferencePlaceholder',
                  })}
                  value={externalRef}
                  onChange={(e) => setExternalRef(e.target.value)}
                />
              </div>
              <div className="form-group d-inline-block">
                <label className="form-label">
                  {intl.formatMessage({ id: 'invoices.accountedFilter' })}
                </label>
                <select
                  className="form-control w-250px form-select"
                  value={
                    accounted === true ? '1' : accounted === false ? '0' : '-1'
                  }
                  onChange={(e) =>
                    setAccounted(
                      e.target.value === '1'
                        ? true
                        : e.target.value === '0'
                          ? false
                          : null
                    )
                  }
                >
                  {accountedOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group w-180px d-inline-block">
                <DateRangeSelector
                  label={intl.formatMessage({ id: 'invoices.filterByDate' })}
                  onSelected={(since, until) => setDateRange({ since, until })}
                  initialSince={dateRange.since}
                  initialUntil={dateRange.until}
                />
              </div>
            </div>
            <div className="d-flex gap-4 mt-4">
              {invoiceEditingUsers?.includes(authUser?.email) && (
                <div>
                  <button
                    className="btn btn-secondary text-dark ms-auto d-inline-block"
                    onClick={changeInvoiceNumber}
                  >
                    <span className="fa-icon fas fa-exclamation-triangle me-3" />
                    {intl.formatMessage({ id: 'invoices.changeNumber' })}
                  </button>
                </div>
              )}
              <button
                className="btn btn-success me-3"
                onClick={() => setVisibleInvoiceAmountModal(true)}
              >
                <span className="fa-icon fas fa-plus me-3" />
                {intl.formatMessage({ id: 'invoices.newCustomInvoice' })}
              </button>
              <button
                className="btn btn-primary"
                onClick={() => setVisibleCreditNoteAmountModal(true)}
              >
                <span className="fa-icon fas fa-plus me-3" />
                {intl.formatMessage({ id: 'invoices.newCustomCreditNote' })}
              </button>
            </div>
          </InvoiceList>
        </div>
      </div>
      <InvoiceNumberModal
        visible={invoiceNumberModalVisible}
        onClose={() => setInvoiceNumberModalVisible(false)}
        onSave={onSaveInvoiceNumber}
        currentNumber={invoices[0]?.number}
      />
      <CreditNoteModal
        invoice={invoiceForCreditNoteModal}
        visible={visibleCreditNoteModal}
        onClose={() => setVisibleCreditNoteModal(false)}
        onCreateCreditNote={createACreditNote}
        loading={isGeneratingCreditNote}
      />
      <AmountInvoiceModal
        kind={AmountInvoiceKind.CreditNote}
        visible={visibleCreditNoteAmountModal}
        onClose={() => setVisibleCreditNoteAmountModal(false)}
        onCreated={onCreatedCreditNote}
      />
      <AmountInvoiceModal
        kind={AmountInvoiceKind.Invoice}
        visible={visibleInvoiceAmountModal}
        onClose={() => setVisibleInvoiceAmountModal(false)}
        onCreated={onCreatedInvoice}
      />
    </>
  );
};

export { InvoicesPage };
